import { FileWarning } from 'lucide-react';

/**
 * An attachment the extractor never opened, said out loud.
 *
 * This exists because of a specific silence. A vendor sent a 16.78 MB
 * proposal PDF, it was past the size the reader could take, and it was
 * dropped to a server log. The planner saw an empty Quotes tab whose own
 * copy promised that attachments land here, concluded no price had been
 * sent, and typed the figure in by hand off the PDF they opened themselves.
 * Nothing on screen was false, and the whole thing was still misleading.
 *
 * So the notice is deliberately *not* styled as an error. Nothing failed
 * that the planner did, and a red panel would read as "this inquiry is
 * broken" when the truthful message is narrower: one file was not read, the
 * thread still has it, here it is. It takes the warm tertiary surface the
 * system already uses for things worth noticing rather than fixing, and it
 * hands over the one action that resolves it — open the message and look.
 */

export interface UnreadAttachment {
  messageId: string;
  filename: string;
  reason: 'too_large' | 'fetch_failed';
  size: number;
}

/** Megabytes at one decimal. The planner is being told why a file was
 *  refused, and "17,596,007 bytes" does not answer that question. */
function megabytes(size: number): string {
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function explain(attachment: UnreadAttachment): string {
  return attachment.reason === 'too_large'
    ? `is ${megabytes(attachment.size)}, past the size that can be read automatically`
    : 'could not be downloaded from Gmail';
}

export default function UnreadAttachmentNotice({
  attachments,
  onOpenMessage,
}: {
  attachments: UnreadAttachment[];
  /** Jumps to the message carrying the file, the same way a quote's
   *  provenance line does. The planner's next move is to read it. */
  onOpenMessage: (messageId: string) => void;
}) {
  if (attachments.length === 0) return null;

  return (
    <div className="rounded-3xl bg-[var(--color-tertiary-container)] p-5">
      <div className="flex gap-3">
        <FileWarning
          aria-hidden
          className="mt-0.5 size-4 shrink-0 text-[var(--color-coverage-strong)]"
        />
        <div className="min-w-0 space-y-2">
          <p className="text-sm font-medium text-[var(--color-coverage-strong)]">
            {attachments.length === 1
              ? 'An attachment on this inquiry was not read'
              : `${attachments.length} attachments on this inquiry were not read`}
          </p>

          <ul className="space-y-1.5">
            {attachments.map((attachment, i) => (
              <li
                key={`${attachment.messageId}-${attachment.filename}-${i}`}
                className="text-sm text-[var(--color-coverage-strong)]/85"
              >
                {/* `break-words`, not `truncate`: the filename is the whole
                    point of the sentence, and a vendor's proposal is named
                    "Katie + Johnny 61227 MVMNT DJs Proposal MR.pdf" — the
                    half a truncation keeps is the half that identifies
                    nothing. */}
                <span className="break-words font-medium">{attachment.filename}</span>{' '}
                {explain(attachment)}.{' '}
                <button
                  type="button"
                  onClick={() => onOpenMessage(attachment.messageId)}
                  className="rounded-sm underline decoration-dotted underline-offset-4
                    transition-colors hover:decoration-solid
                    focus-visible:outline-2 focus-visible:outline-offset-2
                    focus-visible:outline-[var(--color-coverage-strong)]"
                >
                  Open the message
                </button>{' '}
                to check it for pricing.
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
